import SteveModel from '@shell/plugins/steve/steve-class';
import { escapeHtml } from '@shell/utils/string';
import { HCI } from '../types';
import { HCI as HCI_ANNOTATIONS } from '@pkg/harvester/config/labels-annotations';
import { getHarvesterUserName } from '../utils/auth';

const STATUS_DISPLAY = {
  enabled: {
    displayKey: 'generic.enabled',
    color:      'bg-success'
  },
  pending: {
    displayKey: 'generic.inProgress',
    color:      'bg-info'
  },
  disabled: {
    displayKey: 'generic.disabled',
    color:      'bg-warning'
  },
  error: {
    displayKey: 'generic.disabled',
    color:      'bg-warning'
  }
};

/**
 * Class representing PCI Device resource.
 * @extends SteveModal
 */
export default class PCIDevice extends SteveModel {
  get _availableActions() {
    const out = super._availableActions;

    out.push(
      {
        action:     'enablePassthroughBulk',
        enabled:    !this.isEnabling && !this.isvGPUDevice && this.canUpdate,
        icon:       'icon icon-fw icon-dot',
        label:      'Enable Passthrough',
        bulkable:   true,
        bulkAction: 'enablePassthroughBulk',
        weight:     1
      },
      {
        action:   'disablePassthrough',
        enabled:  this.isEnabling && this.claimedByMe && !this.isvGPUDevice && this.canUpdate,
        icon:     'icon icon-fw icon-dot-open',
        label:    'Disable Passthrough',
        bulkable: true,
        weight:   0
      },
    );

    return out;
  }

  get canUpdate() {
    return !!this.linkFor('update');
  }

  get isvGPUDevice() {
    if (!this.vGPUAsPCIDeviceFeatureEnabled) {
      return false;
    }

    return !!this.metadata?.labels?.[HCI_ANNOTATIONS.PARENT_SRIOV_GPU];
  }

  get canYaml() {
    return false;
  }

  get canDelete() {
    return false;
  }

  goToDetail() {
    return false;
  }

  goToEdit() {
    return false;
  }

  get passthroughClaim() {
    const passthroughClaims = this.$getters['all'](HCI.PCI_CLAIM) || [];

    return !!this.status && passthroughClaims.find((req) => req?.spec?.nodeName === this.status?.nodeName && req?.spec?.address === this.status?.address);
  }

  // this is an id for each 'type' of device - there may be multiple instances of device CRs
  get uniqueId() {
    return `${ this.status?.vendorId }:${ this.status?.deviceId }`;
  }

  get claimedBy() {
    return this.passthroughClaim?.spec?.userName;
  }

  get claimedByMe() {
    if (!this.passthroughClaim) {
      return false;
    }

    const userName = getHarvesterUserName(this.$rootGetters);

    return this.claimedBy === userName;
  }

  // isEnabled controls visibility in vm create page & ability to delete claim
  // isEnabling controls ability to add claim
  // there will be a brief period where isEnabling === true && isEnabled === false
  get isEnabled() {
    return !!this.passthroughClaim?.status?.passthroughEnabled;
  }

  get isEnabling() {
    return !!this.passthroughClaim;
  }

  // map status.passthroughEnabled to disabled/enabled & overwrite default dash colors
  get claimStatusDisplay() {
    if (!this.passthroughClaim) {
      return STATUS_DISPLAY.disabled;
    }
    if (this.isEnabled) {
      return STATUS_DISPLAY.enabled;
    }

    return STATUS_DISPLAY.pending;
  }

  get stateDisplay() {
    const t = this.$rootGetters['i18n/t'];

    return t(this.claimStatusDisplay.displayKey);
  }

  get stateBackground() {
    return this.claimStatusDisplay.color;
  }

  // 'enable' passthrough creates the passthrough claim CRD -
  enablePassthroughBulk(resources = this) {
    this.$dispatch('promptModal', {
      resources,
      component: 'EnablePciPassthrough'
    });
  }

  // 'disable' passthrough deletes claim
  // backend should return error if device is in use
  async disablePassthrough() {
    if (!this.allowDisable) {
      this.showDetachWarning();

      return;
    }

    try {
      if (!this.claimedByMe) {
        throw new Error(this.$rootGetters['i18n/t']('harvester.pci.cantUnclaim', { name: escapeHtml(this.metadata.name) }));
      } else {
        await this.passthroughClaim.remove();
      }
    } catch (err) {
      this.$dispatch('growl/fromError', {
        title: this.$rootGetters['i18n/t']('harvester.pci.unclaimError', { name: escapeHtml(this.metadata.name) }),
        err,
      }, { root: true });
    }
  }

  // group device list by node
  get groupByNode() {
    const name = this.status?.nodeName || this.$rootGetters['i18n/t']('generic.none');

    return this.$rootGetters['i18n/t']('resourceTable.groupLabel.node', { name: escapeHtml(name) });
  }

  // group device list by unique device (same vendorid and deviceid)
  get groupByDevice() {
    return this.status?.description;
  }

  get vGPUAsPCIDeviceFeatureEnabled() {
    return this.$rootGetters['harvester-common/getFeatureEnabled']('vGPUAsPCIDevice');
  }

  showDetachWarning() {
    this.$dispatch('growl/warning', {
      title:   this.$rootGetters['i18n/t']('harvester.pci.detachWarning.title'),
      message: this.$rootGetters['i18n/t']('harvester.pci.detachWarning.message'),
      timeout: 5000
    }, { root: true });
  }

  get allowDisable() {
    return this._allowDisable;
  }

  set allowDisable(value) {
    this._allowDisable = value;
  }
}
